import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Tabs,
	Tab,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	LinearProgress,
	IconButton,
	Tooltip,
} from "@mui/material";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import PATAddButton from "../../components/Buttons/PATAddButton";
import TextFieldController from "../../components/inputs/TextFieldController";
import { isValidDateTest, maxLengthMessage } from "../../utils/functions";
import AddEditMaintenanceDialog from "../Maintenance/AddEditMaintenanceDialog";
import {
	createMaintenance,
	updateMaintenance,
} from "../../reducers/MaintenanceReducer";
import { notify } from "../../reducers/NotifierReducer";
import { AssignmentReturn } from "@mui/icons-material";
import { getService } from "../../reducers/service";
import { entityNamesForCustomId, serviceNames } from "../../utils/constants";
import { RegularExpressions } from "../../utils/regular-expressions";

const equipmentSchema = yup.object().shape({
	// General
	equipmentNum: yup
		.string()
		.trim()
		.matches(RegularExpressions.ID, "Invalid Equipment#")
		.max(30, maxLengthMessage(30)),
	status: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.required("Status is required"),
	type: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.required("Type is required"),
	categoryCode: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.required("Category Code is required"),
	propertyNumber: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.required("Property Number is required"),
	assetTag: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	condition: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	serialNumber: yup
		.string()
		.trim()
		.max(100, maxLengthMessage(100))
		.required("Serial Number is required"),
	brand: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	model: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	style: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	color: yup.string().trim().max(30, maxLengthMessage(30)).nullable(),
	description: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),

	// Acquisition
	cost: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	acquiredDate: isValidDateTest("Acquired Date"),
	replaceDate: yup.string().nullable(),
	purchasedFrom: yup
		.string()
		.trim()
		.max(100, maxLengthMessage(100))
		.nullable(),
	fundingSourceId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	lastInspectionDate: yup.string().nullable(),
	auditedBy: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),

	// Assignment
	division: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	location: yup.string().trim().max(200, maxLengthMessage(200)).nullable(),
	vehicleId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	perPersonnelId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	assignedDate: yup.string().nullable(),
	assignedBy: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),

	// Details
	caliber: yup.string().trim().max(30, maxLengthMessage(30)).nullable(),
	sights: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	warrantyInfo: yup
		.string()
		.trim()
		.max(500, maxLengthMessage(500))
		.nullable(),
	comments: yup.string().trim().max(1000, maxLengthMessage(1000)).nullable(),

	serviceRequirements: yup.array().default([]),
});

const TabPanel = ({ children, value, index }) =>
	value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const buildDefaults = (initialData, perPersonnelId = null) => {
	return {
		// General
		equipmentNum: initialData?.equipmentNum || "",
		status: initialData?.status || "",
		type: initialData?.type || "",
		categoryCode: initialData?.categoryCode || "",
		propertyNumber: initialData?.propertyNumber || "",
		assetTag: initialData?.assetTag || "",
		condition: initialData?.condition || "",
		serialNumber: initialData?.serialNumber || "",
		brand: initialData?.brand || "",
		model: initialData?.model || "",
		style: initialData?.style || "",
		color: initialData?.color || "",
		description: initialData?.description || "",

		// Acquisition
		cost: initialData?.cost || "",
		acquiredDate: initialData?.acquiredDate
			? moment(initialData.acquiredDate).format("MM/DD/YYYY")
			: "",
		replaceDate: initialData?.replaceDate
			? moment(initialData.replaceDate).format("MM/DD/YYYY")
			: "",
		purchasedFrom: initialData?.purchasedFrom || "",
		fundingSourceId: initialData?.fundingSourceId
			? initialData.fundingSourceId
			: "",
		lastInspectionDate: initialData?.lastInspectionDate
			? moment(initialData.lastInspectionDate).format("MM/DD/YYYY")
			: "",
		auditedBy: initialData?.auditedBy || "",

		// Assignment
		division: initialData?.division || "",
		location: initialData?.location || "",
		vehicleId: initialData?.vehicleId ? initialData.vehicleId : "",
		perPersonnelId: initialData?.perPersonnelId
			? String(initialData.perPersonnelId)
			: perPersonnelId
				? String(perPersonnelId)
				: "",
		assignedDate: initialData?.assignedDate
			? moment(initialData.assignedDate).format("MM/DD/YYYY")
			: "",
		assignedBy: initialData?.assignedBy || "",

		// Details
		caliber: initialData?.caliber || "",
		sights: initialData?.sights || "",
		warrantyInfo: initialData?.warrantyInfo || "",
		comments: initialData?.comments || "",

		// serviceRequirements = array of ptsCodeId values from junction table
		serviceRequirements: initialData?.serviceRequirements || [],
	};
};

const AddEditEquipmentDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	perPersonnelId,
	isLoadData,
	viewOnly = false,
	// Options
	statusOptions,
	typeOptions,
	categoryCodeOptions,
	conditionOptions,
	divisionOptions,
	employeeOptions,
	vehicleOptions,
	fundingSourceOptions,
	// serviceRequirements = array of {label, value, ptsCodeId, intervalDays, ...}
	serviceRequirements,
	// Assignment history
	assignmentHistory = [],
	// Maintenance data
	maintenanceRecords = [],
	onAddMaintenance,
	onEditMaintenance,
	canManageMaintenance = true,
	onMaintenanceUpdate,
}) => {
	const user = useSelector((state) => state.user);
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData } = useSelector((state) => state.user);
	const dispatch = useDispatch();
	const [tab, setTab] = useState(0);
	const [showMaintModal, setShowMaintModal] = useState(false);
	const [editMaintRec, setEditMaintRec] = useState(null);
	const [maintForm, setMaintForm] = useState({
		date: "",
		performedBy: "",
		cost: "",
		description: "",
		status: "Completed",
		notes: "",
		serviceRequirementId: "",
		fundingSourceId: "",
	});
	const [maintChartOpen, setMaintChartOpen] = useState(true);
	const [maintChartType, setMaintChartType] = useState("bar");

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(equipmentSchema),
		defaultValues: buildDefaults(initialData, perPersonnelId),
		mode: "onChange",
	});

	// Reset form when dialog opens with new data
	useEffect(() => {
		reset(buildDefaults(initialData, perPersonnelId));
		setTab(0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialData, reset, isLoadData]);

	// Reset on close
	useEffect(() => {
		if (!open) {
			reset();
			setTab(0);
		}
	}, [open, reset]);

	const onSubmit = async (data) => {
		let equipmentGeneratedId;
		if (!data.equipmentNum?.trim()) {
			equipmentGeneratedId = await generateCustomId();
		}
		const payload = {
			// General
			equipmentNum: equipmentGeneratedId
				? equipmentGeneratedId
				: data?.equipmentNum?.trim() || null,
			status: data.status || null,
			type: data.type || null,
			categoryCode: data.categoryCode || null,
			propertyNumber: data.propertyNumber?.trim() || null,
			assetTag: data.assetTag?.trim() || null,
			condition: data.condition || null,
			serialNumber: data.serialNumber?.trim() || null,
			brand: data.brand?.trim() || null,
			model: data.model?.trim() || null,
			style: data.style?.trim() || null,
			color: data.color?.trim() || null,
			description: data.description?.trim() || null,

			// Acquisition
			cost: data.cost || null,
			acquiredDate: data.acquiredDate
				? moment(data.acquiredDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			replaceDate: data.replaceDate
				? moment(data.replaceDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			purchasedFrom: data.purchasedFrom?.trim() || null,
			fundingSourceId: data.fundingSourceId || null,
			lastInspectionDate: data.lastInspectionDate
				? moment(data.lastInspectionDate, "MM/DD/YYYY").format(
						"YYYY-MM-DD",
					)
				: null,
			auditedBy: data.auditedBy?.trim() || null,

			// Assignment
			division: data.division || null,
			location: data.location?.trim() || null,
			vehicleId: data.vehicleId || null,
			perPersonnelId: data.perPersonnelId || null,
			assignedDate: data.assignedDate
				? moment(data.assignedDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			assignedBy: data.assignedBy?.trim() || null,

			// Details
			caliber: data.caliber?.trim() || null,
			sights: data.sights?.trim() || null,
			warrantyInfo: data.warrantyInfo?.trim() || null,
			comments: data.comments?.trim() || null,

			// Service requirements = array of ptsCodeId values
			serviceRequirements: data.serviceRequirements || [],

			agencyId: user?.userAgency || null,
		};

		onSave(payload);
		reset();
		onClose();
	};

	// Chart dimensions and helpers
	const W = 600;
	const H = 110;
	const PL = 30; // padding left
	const PR = 10; // padding right
	const PT = 10; // padding top
	const PB = 20; // padding bottom
	const CW = W - PL - PR; // chart width
	const CH = H - PT - PB; // chart height

	// Grid rendering helper
	const renderGrid = (maxCost) => {
		const steps = 4;
		const lines = [];
		for (let i = 0; i <= steps; i++) {
			const y = PT + (CH / steps) * i;
			const val = maxCost * (1 - i / steps);
			lines.push(
				<g key={i}>
					<line
						x1={PL}
						y1={y}
						x2={W - PR}
						y2={y}
						stroke="#e0e0e0"
						strokeWidth={i === steps ? 1.5 : 0.5}
						strokeDasharray={i === steps ? "none" : "2,2"}
					/>
					<text
						x={PL - 5}
						y={y}
						textAnchor="end"
						dominantBaseline="middle"
						style={{
							fontSize: 9,
							fill: "#999",
							fontFamily: "monospace",
						}}
					>
						{fmtCost(val)}
					</text>
				</g>,
			);
		}
		return lines;
	};

	// Bar chart renderer
	const renderBarChart = () => {
		const { buckets } = getMaintChartData();
		const maxCost = Math.max(...buckets.map((b) => b.cost), 1);
		const bn = buckets.length;
		const slot = CW / bn;
		const bw = Math.min(slot * 0.7, 40);

		return (
			<svg
				viewBox={`0 0 ${W} ${H}`}
				preserveAspectRatio="none"
				style={{ width: "100%", height: H, display: "block" }}
			>
				{renderGrid(maxCost)}
				{buckets.map((b, i) => {
					const bh =
						b.cost > 0 ? Math.max(2, (b.cost / maxCost) * CH) : 0;
					const x = PL + i * slot + slot / 2 - bw / 2;
					const y = PT + CH - bh;

					return (
						<g key={i}>
							<rect
								x={x}
								y={y}
								width={bw}
								height={bh}
								fill={b.cost > 0 ? "#1976d2" : "#e0e0e0"}
								rx={2}
								opacity={0.85}
							/>
							{b.cost > 0 && (
								<rect
									x={x}
									y={PT}
									width={bw}
									height={CH}
									fill="transparent"
									style={{ cursor: "default" }}
								>
									<title>
										{b.label}: {fmtCost(b.cost)}
									</title>
								</rect>
							)}
							{(bn <= 16 || i % Math.ceil(bn / 12) === 0) && (
								<text
									x={PL + i * slot + slot / 2}
									y={H - 2}
									textAnchor="middle"
									style={{
										fontSize: 7.5,
										fill: "#999",
										fontFamily: "monospace",
									}}
								>
									{b.label}
								</text>
							)}
						</g>
					);
				})}
			</svg>
		);
	};

	// Line chart renderer
	const renderLineChart = () => {
		const { buckets } = getMaintChartData();
		const maxCost = Math.max(...buckets.map((b) => b.cost), 1);
		const bn = buckets.length;

		const pts = buckets.map((b, i) => ({
			x: PL + (i / Math.max(bn - 1, 1)) * CW,
			y: PT + CH * (1 - b.cost / maxCost),
			...b,
		}));

		const poly = pts.map((p) => `${p.x},${p.y}`).join(" ");
		const area =
			pts.length > 1
				? `M ${pts[0].x},${PT + CH} ` +
					pts.map((p) => `L ${p.x},${p.y}`).join(" ") +
					` L ${pts[pts.length - 1].x},${PT + CH} Z`
				: "";

		return (
			<svg
				viewBox={`0 0 ${W} ${H}`}
				preserveAspectRatio="none"
				style={{ width: "100%", height: H, display: "block" }}
			>
				<defs>
					<linearGradient id="maintGrad" x1="0" y1="0" x2="0" y2="1">
						<stop
							offset="0%"
							stopColor="#1976d2"
							stopOpacity="0.25"
						/>
						<stop
							offset="100%"
							stopColor="#1976d2"
							stopOpacity="0.02"
						/>
					</linearGradient>
				</defs>
				{renderGrid(maxCost)}
				{area && <path d={area} fill="url(#maintGrad)" />}
				{pts.length > 1 && (
					<polyline
						points={poly}
						fill="none"
						stroke="#1976d2"
						strokeWidth={1.8}
						strokeLinejoin="round"
						strokeLinecap="round"
					/>
				)}
				{pts.map((p, i) => (
					<g key={i}>
						<circle
							cx={p.x}
							cy={p.y}
							r={3}
							fill={p.cost > 0 ? "#1976d2" : "#e0e0e0"}
							stroke="#fff"
							strokeWidth={1.5}
						/>
						{p.cost > 0 && (
							<title>
								{p.label}: {fmtCost(p.cost)}
							</title>
						)}
						{(bn <= 16 || i % Math.ceil(bn / 12) === 0) && (
							<text
								x={p.x}
								y={H - 2}
								textAnchor="middle"
								style={{
									fontSize: 7.5,
									fill: "#999",
									fontFamily: "monospace",
								}}
							>
								{p.label}
							</text>
						)}
					</g>
				))}
			</svg>
		);
	};

	// Main chart renderer
	const renderMaintenanceChart = () => {
		return maintChartType === "bar" ? renderBarChart() : renderLineChart();
	};

	// ── Service requirements
	// assignedReqs = array of ptsCodeId numbers stored in junction table
	const assignedReqs = watch("serviceRequirements") || [];
	// activeReqs = array of {label, value, ptsCodeId, intervalDays, Code, Value, Unit, ...}
	const activeReqs = serviceRequirements || [];

	const toggleReq = (ptsCodeId) => {
		const next = assignedReqs.includes(ptsCodeId)
			? assignedReqs.filter((x) => x !== ptsCodeId)
			: [...assignedReqs, ptsCodeId];
		setValue("serviceRequirements", next, { shouldDirty: true });
	};

	// ── Assignment history ──
	const eqHistory = initialData?.id
		? [
				...assignmentHistory.filter(
					(h) => h.equipmentId === initialData.id,
				),
			].sort(
				(a, b) => new Date(b.assignedDate) - new Date(a.assignedDate),
			)
		: [];

	// ── Maintenance records for this equipment
	const eqMaintenance = initialData?.id
		? maintenanceRecords.filter((m) => m.equipmentId === initialData.id)
		: [];

	// ── Maintenance modal helpers
	const openAddMaint = () => {
		setEditMaintRec(null);
		setMaintForm({
			date: moment().format("YYYY-MM-DD"),
			performedBy: "",
			cost: "",
			description: "",
			status: "Completed",
			notes: "",
			serviceRequirementId: "",
			fundingSourceId: "",
			equipmentId: initialData?.id,
		});
		setShowMaintModal(true);
	};

	const openEditMaint = (record) => {
		setEditMaintRec(record);
		setMaintForm({
			date: record.date ? moment(record.date).format("YYYY-MM-DD") : "",
			performedBy: record.performedBy || "",
			cost: record.cost ?? "",
			description: record.description || "",
			status: record.status || "Completed",
			notes: record.notes || "",
			equipmentId: initialData?.id,
			statusDate: record?.statusDate
				? moment(record.statusDate).format("MM/DD/YYYY")
				: "",
			perPersonnelId: record?.perPersonnelId
				? String(record?.perPersonnelId)
				: "",
			serviceRequirementId: record.serviceRequirementId
				? record.serviceRequirementId
				: "",
			fundingSourceId: record.fundingSourceId
				? record.fundingSourceId
				: "",
		});
		setShowMaintModal(true);
	};

	const handleSave = async (payload) => {
		try {
			if (editMaintRec) {
				await dispatch(
					updateMaintenance(
						editMaintRec.id,
						payload,
						userData.user.ptsUserID,
					),
				);
			} else {
				await dispatch(
					createMaintenance(
						payload,
						userAgency,
						userData.user.ptsUserID,
					),
				);
			}
			setShowMaintModal(false);
			setEditMaintRec(null);

			// Call the callback to refresh the data
			if (onMaintenanceUpdate) {
				await onMaintenanceUpdate();
			}
			dispatch(
				notify("Maintenance record saved successfully", "success"),
			);
		} catch (error) {
			dispatch(notify("Failed to save maintenance record", "error"));
		}
	};

	// ── Service requirement status calculation
	const getReqStatus = (ptsCodeId) => {
		const sr = activeReqs.find((r) => r.ptsCodeId === ptsCodeId);
		if (!sr) return null;

		const intervalDays = sr.intervalDays || Number(sr.Value) || 365;

		// Filter maintenance records that fulfill this service requirement
		const reqHistory = eqMaintenance.filter(
			(m) =>
				m.serviceRequirementId === ptsCodeId &&
				m.status === "Completed",
		);
		const last = [...reqHistory].sort(
			(a, b) => new Date(b.date) - new Date(a.date),
		)[0];
		const lastDate = last?.date || initialData?.acquiredDate || null;

		if (!lastDate) {
			return {
				remaining: 0,
				overdue: true,
				pct: 100,
				lastDate: null,
				daysSince: null,
			};
		}

		const daysSince = moment().diff(moment(lastDate), "days");
		const remaining = intervalDays - daysSince;
		const pct = Math.max(
			0,
			Math.min(100, (daysSince / intervalDays) * 100),
		);
		const overdue = remaining <= 0;
		const dueSoon = !overdue && remaining <= intervalDays * 0.1;

		return { remaining, overdue, dueSoon, pct, lastDate, daysSince };
	};

	// Helper to get intervalDays from a service requirement
	const getIntervalDays = (sr) => {
		return sr.intervalDays || Number(sr.Value) || 365;
	};

	// Helper to get interval label
	const getIntervalLabel = (days) => {
		if (days === 365) return " (Annual)";
		if (days === 180) return " (Semi-annual)";
		if (days === 90) return " (Quarterly)";
		if (days === 30) return " (Monthly)";
		return "";
	};

	// ── Maintenance chart calculation ─────────
	const getMaintChartData = () => {
		const chartRecs = eqMaintenance.filter((m) => m.cost > 0 && m.date);
		const totalCost = chartRecs.reduce((s, m) => s + (m.cost || 0), 0);

		let startDate = initialData?.acquiredDate
			? moment(initialData.acquiredDate).startOf("quarter")
			: moment().subtract(11, "months").startOf("month");

		const endDate = moment();
		const buckets = [];

		let cur = moment(startDate);
		while (cur.isBefore(endDate) || cur.isSame(endDate, "quarter")) {
			const qEnd = moment(cur).add(3, "months");
			const label = `Q${Math.floor(cur.month() / 3) + 1} '${String(cur.year()).slice(2)}`;
			const cost = chartRecs
				.filter((m) => {
					const d = moment(m.date);
					return (
						d.isSameOrAfter(cur, "day") && d.isBefore(qEnd, "day")
					);
				})
				.reduce((s, m) => s + (m.cost || 0), 0);

			buckets.push({ label, cost });
			cur.add(3, "months");

			if (buckets.length > 12) break;
		}

		return { buckets, totalCost };
	};

	const dialogTitle = initialData
		? viewOnly
			? `View Equipment — ${initialData.serialNumber}`
			: `Edit Equipment — ${initialData.serialNumber}`
		: "Add Equipment";

	const fmtDate = (date) => (date ? moment(date).format("MM/DD/YYYY") : "—");
	const fmtCost = (n) =>
		n
			? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
			: "—";

	const generateCustomId = async () => {
		const data = {
			EntityName: entityNamesForCustomId.EquipmentId,
			agencyID: userAgency,
		};
		const service = getService(serviceNames.customId);
		service.timeout = 20000;

		const id = await service.create(data);
		if (id?.includes("Agency ID does not match")) {
			dispatch(
				notify(
					"Unable to generate Equipment ID because the Agency ID does not match.",
					"warning",
				),
			);
			return null;
		}
		return id;
	};

	useEffect(() => {
		setValue("equipmentNum", "");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency]);

	const handleAutoGenerateClick = async () => {
		const id = await generateCustomId();
		setValue("equipmentNum", id, { shouldValidate: true });
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
				<DialogTitle>{dialogTitle}</DialogTitle>

				<Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
					<Tabs
						value={tab}
						onChange={(_, v) => setTab(v)}
						variant="scrollable"
						scrollButtons="auto"
					>
						<Tab label="General" />
						<Tab label="Acquisition" />
						<Tab label="Assignment" />
						<Tab label="Details" />
						<Tab label="Service Req." />
						<Tab label="Maintenance" />
					</Tabs>
				</Box>

				<DialogContent
					sx={{
						pointerEvents: viewOnly ? "none" : undefined,
						opacity: viewOnly ? 0.75 : undefined,
					}}
				>
					<Box
						component="form"
						onSubmit={handleSubmit(onSubmit)}
						noValidate
					>
						{/* ── GENERAL ── */}
						<TabPanel value={tab} index={0}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(3, 1fr)",
									gap: 2,
								}}
							>
								<Box display="flex" alignItems="center">
									<Box flexGrow={1}>
										<TextFieldController
											name="equipmentNum"
											label="Equipment#"
											required={true}
											control={control}
											errors={errors}
										/>
									</Box>
									<Box>
										<Tooltip title="Auto generate equipment number">
											<IconButton
												color="primary"
												aria-label="Auto generate equipment number"
												size="large"
												onClick={
													handleAutoGenerateClick
												}
											>
												<AssignmentReturn />
											</IconButton>
										</Tooltip>
									</Box>
								</Box>
								<Box>
									<DropdownController
										name="status"
										label="Status*"
										options={statusOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<DropdownController
										name="type"
										label="Type*"
										options={typeOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<DropdownController
										name="categoryCode"
										label="Category Code*"
										options={categoryCodeOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="propertyNumber"
										label="Property Number*"
										control={control}
										errors={errors}
										placeholder="PROP-2024-0001"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="assetTag"
										label="Asset Tag"
										control={control}
										errors={errors}
										placeholder="e.g. AT-4421"
									/>
								</Box>
								<Box>
									<DropdownController
										name="condition"
										label="Condition"
										options={conditionOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="serialNumber"
										label="Serial Number*"
										control={control}
										errors={errors}
										placeholder="e.g. SN-12345"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="brand"
										label="Brand / Make"
										control={control}
										errors={errors}
										placeholder="e.g. Motorola"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="model"
										label="Model"
										control={control}
										errors={errors}
										placeholder="e.g. APX 8000"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="style"
										label="Style"
										control={control}
										errors={errors}
										placeholder="e.g. Portable, In-Vehicle"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="color"
										label="Color"
										control={control}
										errors={errors}
										placeholder="e.g. Black"
									/>
								</Box>
								<Box sx={{ gridColumn: "1 / -1" }}>
									<TextFieldController
										name="description"
										label="Description"
										control={control}
										errors={errors}
										placeholder="Brief description of item"
										multiline
										rows={2}
									/>
								</Box>
							</Box>
						</TabPanel>

						{/* ── ACQUISITION ── */}
						<TabPanel value={tab} index={1}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 2,
								}}
							>
								<Box>
									<TextFieldController
										name="cost"
										label="Cost ($)"
										control={control}
										errors={errors}
										type="number"
										placeholder="0.00"
									/>
								</Box>
								<Box>
									<DatePickerController
										name="acquiredDate"
										label="Acquired Date"
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<DatePickerController
										name="replaceDate"
										label="Replace Date"
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="purchasedFrom"
										label="Purchased From"
										control={control}
										errors={errors}
										placeholder="Vendor / supplier name"
									/>
								</Box>
								<Box sx={{ gridColumn: "1 / -1" }}>
									<DropdownController
										name="fundingSourceId"
										label="Funding Source"
										options={fundingSourceOptions || []}
										control={control}
										errors={errors}
										placeholder="— None —"
									/>
								</Box>

								{/* Audit Section */}
								<Box
									sx={{
										gridColumn: "1 / -1",
										pt: 2,
										borderTop: 1,
										borderColor: "divider",
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 2,
										}}
									>
										<Typography
											variant="caption"
											fontWeight={700}
											color="text.secondary"
											sx={{
												textTransform: "uppercase",
												letterSpacing: "0.06em",
											}}
										>
											Audit
										</Typography>
										{!viewOnly && (
											<PATAddButton
												onClick={() => {
													setValue(
														"lastInspectionDate",
														moment().format(
															"MM/DD/YYYY",
														),
														{ shouldDirty: true },
													);
													setValue(
														"auditedBy",
														user?.userData?.user
															?.Username ||
															"Current User",
														{ shouldDirty: true },
													);
												}}
												tooltipText="Audit Item"
												size="small"
											/>
										)}
									</Box>
									<Box
										sx={{
											display: "grid",
											gridTemplateColumns:
												"repeat(2, 1fr)",
											gap: 2,
										}}
									>
										<Box>
											<DatePickerController
												name="lastInspectionDate"
												label="Last Audit Date"
												control={control}
												errors={errors}
											/>
										</Box>
										<Box>
											<TextFieldController
												name="auditedBy"
												label="Audited By"
												control={control}
												errors={errors}
												placeholder="Audited by"
											/>
										</Box>
									</Box>
								</Box>
							</Box>
						</TabPanel>

						{/* ── ASSIGNMENT ── */}
						<TabPanel value={tab} index={2}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 2,
								}}
							>
								<Box>
									<DropdownController
										name="division"
										label="Division"
										options={divisionOptions || []}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="location"
										label="Location"
										control={control}
										errors={errors}
										placeholder="e.g. Vehicle P-101, Armory Rack 3"
									/>
								</Box>
								<Box>
									<DropdownController
										name="vehicleId"
										label="Assigned Vehicle"
										options={vehicleOptions || []}
										control={control}
										errors={errors}
										placeholder="— None —"
									/>
								</Box>
								<Box>
									<DropdownController
										name="perPersonnelId"
										label="Assigned Employee"
										options={employeeOptions || []}
										control={control}
										errors={errors}
										placeholder="— None —"
									/>
								</Box>
								<Box>
									<DatePickerController
										name="assignedDate"
										label="Assigned Date"
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<TextFieldController
										name="assignedBy"
										label="Assigned By"
										control={control}
										errors={errors}
										placeholder="Name / role of assigning employee"
									/>
								</Box>
							</Box>

							{/* Assignment History */}
							{initialData?.id && (
								<Box sx={{ mt: 3 }}>
									<Typography
										variant="caption"
										fontWeight={700}
										color="text.secondary"
										sx={{
											textTransform: "uppercase",
											letterSpacing: "0.06em",
											mb: 1,
											display: "block",
										}}
									>
										Assignment History
										<Typography
											component="span"
											sx={{
												fontWeight: 400,
												textTransform: "none",
												ml: 1,
												color: "text.disabled",
											}}
										>
											{eqHistory.length} record
											{eqHistory.length !== 1 ? "s" : ""}
										</Typography>
									</Typography>

									{eqHistory.length === 0 ? (
										<Box
											sx={{
												p: 2,
												textAlign: "center",
												color: "text.disabled",
												fontSize: 12,
												bgcolor: "background.default",
												borderRadius: 1,
												border: 1,
												borderColor: "divider",
											}}
										>
											No assignment history yet
										</Box>
									) : (
										<TableContainer
											sx={{
												maxHeight: 220,
												border: 1,
												borderColor: "divider",
												borderRadius: 1,
											}}
										>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														{[
															"Employee",
															"Vehicle",
															"Location",
															"Assigned",
															"Unassigned",
															"Days",
															"By",
														].map((h) => (
															<TableCell
																key={h}
																sx={{
																	fontSize: 10,
																	fontWeight: 700,
																	textTransform:
																		"uppercase",
																}}
															>
																{h}
															</TableCell>
														))}
													</TableRow>
												</TableHead>
												<TableBody>
													{eqHistory.map((h) => {
														const emp =
															employeeOptions?.find(
																(e) =>
																	String(
																		e.value,
																	) ===
																	String(
																		h.perPersonnelId,
																	),
															);
														const veh =
															vehicleOptions?.find(
																(v) =>
																	String(
																		v.value,
																	) ===
																	String(
																		h.vehicleId,
																	),
															);
														const isCurrent =
															!h.unassignedDate;
														const dur = Math.round(
															(new Date(
																h.unassignedDate ||
																	new Date(),
															) -
																new Date(
																	h.assignedDate,
																)) /
																86400000,
														);

														return (
															<TableRow
																key={h.id}
																hover
															>
																<TableCell
																	sx={{
																		fontWeight: 600,
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: 0.5,
																	}}
																>
																	{isCurrent && (
																		<Box
																			sx={{
																				width: 6,
																				height: 6,
																				borderRadius:
																					"50%",
																				bgcolor:
																					"success.main",
																			}}
																		/>
																	)}
																	{emp?.label ||
																		"—"}
																</TableCell>
																<TableCell
																	sx={{
																		fontFamily:
																			"monospace",
																		fontSize: 11,
																		color: "text.secondary",
																	}}
																>
																	{veh?.label ||
																		"—"}
																</TableCell>
																<TableCell
																	sx={{
																		color: "text.secondary",
																	}}
																>
																	{h.location ||
																		"—"}
																</TableCell>
																<TableCell
																	sx={{
																		fontFamily:
																			"monospace",
																		fontSize: 11,
																	}}
																>
																	{fmtDate(
																		h.assignedDate,
																	)}
																</TableCell>
																<TableCell>
																	{isCurrent ? (
																		<Chip
																			label="Current"
																			size="small"
																			color="success"
																			sx={{
																				fontSize: 10,
																				height: 20,
																			}}
																		/>
																	) : (
																		<Box
																			sx={{
																				fontFamily:
																					"monospace",
																				fontSize: 11,
																			}}
																		>
																			{fmtDate(
																				h.unassignedDate,
																			)}
																		</Box>
																	)}
																</TableCell>
																<TableCell
																	sx={{
																		fontFamily:
																			"monospace",
																	}}
																>
																	{dur}d
																</TableCell>
																<TableCell
																	sx={{
																		color: "text.secondary",
																	}}
																>
																	{h.assignedBy ||
																		"—"}
																</TableCell>
															</TableRow>
														);
													})}
												</TableBody>
											</Table>
										</TableContainer>
									)}
								</Box>
							)}
						</TabPanel>

						{/* ── DETAILS ── */}
						<TabPanel value={tab} index={3}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 2,
								}}
							>
								<Box>
									<TextFieldController
										name="caliber"
										label="Caliber"
										control={control}
										errors={errors}
										placeholder="e.g. 9mm, 5.56mm NATO, .40 S&W"
									/>
								</Box>
								<Box>
									<TextFieldController
										name="sights"
										label="Sights"
										control={control}
										errors={errors}
										placeholder="e.g. Iron Sights, Aimpoint PRO"
									/>
								</Box>
								<Box sx={{ gridColumn: "1 / -1" }}>
									<TextFieldController
										name="warrantyInfo"
										label="Warranty Information"
										control={control}
										errors={errors}
										placeholder="Warranty terms, expiration, contact..."
										multiline
										rows={3}
									/>
								</Box>
								<Box sx={{ gridColumn: "1 / -1" }}>
									<TextFieldController
										name="comments"
										label="Comments"
										control={control}
										errors={errors}
										placeholder="Additional notes or observations..."
										multiline
										rows={3}
									/>
								</Box>
							</Box>
						</TabPanel>

						{/* ── SERVICE REQUIREMENTS ── */}
						<TabPanel value={tab} index={4}>
							<Typography
								variant="caption"
								fontWeight={700}
								color="text.secondary"
								sx={{
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									mb: 2,
									display: "block",
								}}
							>
								Assign Service Requirements
								<Typography
									component="span"
									sx={{
										fontWeight: 400,
										textTransform: "none",
										ml: 1,
										color: "text.disabled",
										fontSize: 11,
									}}
								>
									Day-based intervals tracked against
									maintenance history
								</Typography>
							</Typography>

							{activeReqs.length === 0 ? (
								<Box
									sx={{
										p: 3,
										textAlign: "center",
										color: "text.disabled",
										fontSize: 12,
										bgcolor: "background.default",
										borderRadius: 1,
										border: 1,
										borderColor: "divider",
									}}
								>
									No service requirements defined. Add them in
									Code Maintenance → Equipment Service
									Requirements.
								</Box>
							) : (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 1.5,
										mb: 3,
									}}
								>
									{activeReqs.map((sr) => {
										// Use ptsCodeId for comparison
										const checked = assignedReqs.includes(
											sr.ptsCodeId,
										);
										const intervalDays =
											getIntervalDays(sr);

										return (
											<Box
												key={sr.ptsCodeId}
												onClick={() =>
													!viewOnly &&
													toggleReq(sr.ptsCodeId)
												}
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1.5,
													p: "10px 14px",
													bgcolor: checked
														? "rgba(63, 131, 245, 0.08)"
														: "background.paper",
													border: "1px solid",
													borderColor: checked
														? "primary.main"
														: "divider",
													borderRadius: 1,
													cursor: viewOnly
														? "default"
														: "pointer",
													transition: "all 0.12s",
													"&:hover": viewOnly
														? {}
														: {
																borderColor:
																	"primary.main",
															},
												}}
											>
												<input
													type="checkbox"
													checked={checked}
													onChange={() =>
														!viewOnly &&
														toggleReq(sr.ptsCodeId)
													}
													style={{
														width: 18,
														height: 18,
														cursor: viewOnly
															? "default"
															: "pointer",
													}}
												/>
												<Box sx={{ flex: 1 }}>
													<Typography
														variant="body2"
														fontWeight={700}
													>
														{sr.label || sr.Code}
													</Typography>
													<Typography
														variant="caption"
														color="text.secondary"
													>
														Every {intervalDays} day
														{intervalDays !== 1
															? "s"
															: ""}
														{getIntervalLabel(
															intervalDays,
														)}
													</Typography>
												</Box>
												{checked && (
													<Chip
														label="Active"
														size="small"
														color="primary"
														sx={{
															fontSize: 10,
															height: 20,
														}}
													/>
												)}
											</Box>
										);
									})}
								</Box>
							)}

							{/* Service Requirement Status */}
							{initialData?.id && assignedReqs.length > 0 && (
								<Box>
									<Typography
										variant="caption"
										fontWeight={700}
										color="text.secondary"
										sx={{
											textTransform: "uppercase",
											letterSpacing: "0.06em",
											mb: 1.5,
											display: "block",
										}}
									>
										Current Status
									</Typography>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: 1,
										}}
									>
										{assignedReqs.map((ptsCodeId) => {
											// Find by ptsCodeId
											const sr = activeReqs.find(
												(r) =>
													r.ptsCodeId === ptsCodeId,
											);
											if (!sr) return null;

											const status =
												getReqStatus(ptsCodeId);
											if (!status) return null;

											const color = status.overdue
												? "error.main"
												: status.dueSoon
													? "warning.main"
													: "success.main";

											return (
												<Box
													key={ptsCodeId}
													sx={{
														p: 1.5,
														bgcolor:
															"background.paper",
														border: 1,
														borderColor: "divider",
														borderRadius: 1,
													}}
												>
													<Box
														sx={{
															display: "flex",
															justifyContent:
																"space-between",
															alignItems:
																"center",
															mb: 0.5,
														}}
													>
														<Typography
															variant="body2"
															fontWeight={600}
														>
															{sr.label ||
																sr.Code}
														</Typography>
														<Typography
															variant="caption"
															fontWeight={700}
															color={color}
														>
															{status.overdue
																? `${Math.abs(status.remaining)}d overdue`
																: `${status.remaining}d left`}
														</Typography>
													</Box>
													<LinearProgress
														variant="determinate"
														value={status.pct}
														sx={{
															height: 4,
															borderRadius: 1,
															bgcolor: "grey.200",
															"& .MuiLinearProgress-bar":
																{
																	bgcolor:
																		color,
																},
														}}
													/>
													<Typography
														variant="caption"
														color="text.disabled"
														sx={{
															mt: 0.5,
															display: "block",
														}}
													>
														{status.lastDate
															? `Last: ${fmtDate(status.lastDate)} (${status.daysSince}d ago)`
															: "Never serviced"}
													</Typography>
												</Box>
											);
										})}
									</Box>
								</Box>
							)}
						</TabPanel>

						{/* ── MAINTENANCE ── */}
						<TabPanel value={tab} index={5}>
							{initialData?.id && (
								<Box
									sx={{
										mb: 2,
										pb: 2,
										borderBottom: 1,
										borderColor: "divider",
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1,
											pb: 1,
										}}
									>
										<Typography
											variant="caption"
											fontWeight={700}
											color="text.secondary"
											sx={{
												textTransform: "uppercase",
												letterSpacing: "0.06em",
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											🔩 Maintenance Costs
											<Typography
												component="span"
												sx={{
													fontFamily: "monospace",
													fontWeight: 400,
													textTransform: "none",
													color: "text.disabled",
													fontSize: 11,
												}}
											>
												{fmtCost(
													getMaintChartData()
														.totalCost,
												)}{" "}
												total · {eqMaintenance.length}{" "}
												record
												{eqMaintenance.length !== 1
													? "s"
													: ""}
											</Typography>
										</Typography>

										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
											}}
										>
											{maintChartOpen && (
												<Box
													sx={{
														display: "flex",
														gap: 0.5,
													}}
												>
													<IconButton
														size="small"
														onClick={() =>
															setMaintChartType(
																"bar",
															)
														}
														sx={{
															bgcolor:
																maintChartType ===
																"bar"
																	? "primary.main"
																	: "background.paper",
															color:
																maintChartType ===
																"bar"
																	? "#fff"
																	: "text.secondary",
															border: 1,
															borderColor:
																"divider",
															borderRadius: 1,
															width: 28,
															height: 28,
															fontSize: 14,
															"&:hover": {
																bgcolor:
																	maintChartType ===
																	"bar"
																		? "primary.dark"
																		: "grey.100",
															},
														}}
														title="Bar chart"
													>
														☲
													</IconButton>
													<IconButton
														size="small"
														onClick={() =>
															setMaintChartType(
																"line",
															)
														}
														sx={{
															bgcolor:
																maintChartType ===
																"line"
																	? "primary.main"
																	: "background.paper",
															color:
																maintChartType ===
																"line"
																	? "#fff"
																	: "text.secondary",
															border: 1,
															borderColor:
																"divider",
															borderRadius: 1,
															width: 28,
															height: 28,
															fontSize: 14,
															"&:hover": {
																bgcolor:
																	maintChartType ===
																	"line"
																		? "primary.dark"
																		: "grey.100",
															},
														}}
														title="Line chart"
													>
														⤴
													</IconButton>
												</Box>
											)}
											{!maintChartOpen && (
												<Typography
													variant="caption"
													sx={{
														fontSize: 10,
														color: "text.disabled",
														fontStyle: "italic",
													}}
												>
													expand for chart
												</Typography>
											)}
											<IconButton
												size="small"
												onClick={() =>
													setMaintChartOpen((o) => !o)
												}
												sx={{
													fontSize: 14,
													transition:
														"transform 0.15s",
													transform: maintChartOpen
														? "rotate(0deg)"
														: "rotate(-90deg)",
												}}
											>
												▾
											</IconButton>
										</Box>
									</Box>

									{maintChartOpen && (
										<Box>
											{eqMaintenance.filter(
												(m) => m.cost > 0,
											).length > 0 ? (
												renderMaintenanceChart()
											) : (
												<Box
													sx={{
														height: 96,
														display: "flex",
														alignItems: "center",
														justifyContent:
															"center",
														color: "text.disabled",
														fontSize: 11,
													}}
												>
													No maintenance costs
													recorded
												</Box>
											)}
										</Box>
									)}
								</Box>
							)}

							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 2,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
									}}
								>
									Maintenance History
									{eqMaintenance.length > 0 && (
										<Typography
											component="span"
											sx={{
												fontWeight: 400,
												color: "text.disabled",
												ml: 1,
											}}
										>
											({eqMaintenance.length})
										</Typography>
									)}
								</Typography>
								{initialData?.id &&
									canManageMaintenance &&
									!viewOnly && (
										<PATAddButton
											onClick={openAddMaint}
											tooltipText="Add Maintenance Record"
											size="small"
										/>
									)}
							</Box>

							{!initialData?.id ? (
								<Box
									sx={{
										p: 3,
										textAlign: "center",
										color: "text.disabled",
										fontSize: 12,
										bgcolor: "background.default",
										borderRadius: 1,
										border: 1,
										borderColor: "divider",
									}}
								>
									Save the equipment record first to log
									maintenance
								</Box>
							) : eqMaintenance.length === 0 ? (
								<Box
									sx={{
										p: 3,
										textAlign: "center",
										color: "text.disabled",
										fontSize: 12,
										bgcolor: "background.default",
										borderRadius: 1,
										border: 1,
										borderColor: "divider",
									}}
								>
									No maintenance records yet
								</Box>
							) : (
								<TableContainer
									sx={{
										maxHeight: 280,
										border: 1,
										borderColor: "divider",
										borderRadius: 1,
									}}
								>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												{[
													"Date",
													"Description",
													"Performed By",
													"Cost",
													"Req",
													"Status",
												].map((h) => (
													<TableCell
														key={h}
														sx={{
															fontSize: 10,
															fontWeight: 700,
															textTransform:
																"uppercase",
														}}
													>
														{h}
													</TableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{eqMaintenance.map((m) => {
												// Find service requirement by ptsCodeId matching serviceRequirementId
												const sr = activeReqs.find(
													(r) =>
														r.ptsCodeId ===
														m.serviceRequirementId,
												);
												return (
													<TableRow
														key={m.id}
														hover
														onClick={() =>
															canManageMaintenance &&
															openEditMaint(m)
														}
														sx={{
															cursor: canManageMaintenance
																? "pointer"
																: "default",
															"&:nth-of-type(odd)":
																{
																	bgcolor:
																		"grey.50",
																},
														}}
													>
														<TableCell
															sx={{
																fontFamily:
																	"monospace",
																fontSize: 11,
																whiteSpace:
																	"nowrap",
															}}
														>
															{fmtDate(m.date)}
														</TableCell>
														<TableCell
															sx={{
																maxWidth: 180,
															}}
														>
															<Typography
																variant="body2"
																noWrap
															>
																{m.description ||
																	"—"}
															</Typography>
															{m.notes && (
																<Typography
																	variant="caption"
																	color="text.disabled"
																>
																	{m.notes}
																</Typography>
															)}
														</TableCell>
														<TableCell
															sx={{
																color: "text.secondary",
																whiteSpace:
																	"nowrap",
															}}
														>
															{m.performedBy ||
																"—"}
														</TableCell>
														<TableCell
															sx={{
																fontFamily:
																	"monospace",
																whiteSpace:
																	"nowrap",
															}}
														>
															{fmtCost(m.cost)}
														</TableCell>
														<TableCell>
															{sr ? (
																<Chip
																	label={
																		sr.label ||
																		sr.Code
																	}
																	size="small"
																	color="primary"
																	sx={{
																		fontSize: 10,
																		height: 20,
																	}}
																/>
															) : (
																<Typography
																	variant="body2"
																	color="text.disabled"
																>
																	—
																</Typography>
															)}
														</TableCell>
														<TableCell>
															<Chip
																label={m.status}
																size="small"
																color={
																	m.status ===
																	"Completed"
																		? "success"
																		: m.status ===
																			  "In Progress"
																			? "info"
																			: m.status ===
																				  "Pending"
																				? "warning"
																				: m.status ===
																					  "Cancelled"
																					? "default"
																					: "primary"
																}
																sx={{
																	fontSize: 10,
																	height: 20,
																}}
															/>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</TabPanel>
					</Box>
				</DialogContent>

				<DialogActions sx={{ px: 3, pb: 2 }}>
					{!viewOnly && (
						<PATSaveButton
							onClick={handleSubmit(onSubmit)}
							tooltipText="Save"
							disabled={!isValid}
						/>
					)}
					<PATCloseButton onClick={onClose} tooltipText="Close" />
				</DialogActions>
			</Dialog>

			{/* ── Maintenance Record Dialog ── */}

			<AddEditMaintenanceDialog
				open={showMaintModal}
				onClose={() => {
					setShowMaintModal(false);
				}}
				onSave={handleSave}
				initialData={maintForm}
				employeeOptions={employeeOptions}
				serviceRequirements={serviceRequirements || []}
				fundingSourceOptions={fundingSourceOptions}
				hideEquipment
			/>
		</>
	);
};

export default AddEditEquipmentDialog;
